import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Award, Download, Calendar, User, BookOpen, Shield, AlertTriangle, Copy } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const ValidarCertificado = () => {
  const { credentialId } = useParams();
  const [certificado, setCertificado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [valido, setValido] = useState(false);

  useEffect(() => {
    if (credentialId) {
      validarCertificado();
    }
  }, [credentialId]);

  const validarCertificado = async () => {
    try {
      const response = await fetch(`http://localhost:5000/api/validate-certificate/${credentialId}`);
      const data = await response.json();

      if (response.ok && data.valido) {
        setCertificado(data.certificado);
        setValido(true);
      } else {
        setError(data.erro || 'Certificado não encontrado');
        setValido(false);
      }
    } catch (error) {
      console.error('Erro:', error);
      setError('Erro ao validar certificado');
      setValido(false);
    } finally {
      setLoading(false);
    }
  };

  const copiarCredencial = () => {
    navigator.clipboard.writeText(credentialId);
    alert('Credencial copiada para a área de transferência!');
  };

  const baixarCertificado = () => {
    if (certificado?.pdf_url) {
      window.open(`http://localhost:5000${certificado.pdf_url}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 text-lg">Validando certificado...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-2xl ${
            valido 
              ? 'bg-gradient-to-br from-green-500 to-green-600' 
              : 'bg-gradient-to-br from-red-500 to-red-600'
          }`}>
            {valido ? (
              <CheckCircle className="w-10 h-10 text-white" />
            ) : (
              <XCircle className="w-10 h-10 text-white" />
            )}
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4">
            Validação de Certificado
          </h1>
          
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-6">
            {valido 
              ? 'Certificado válido e verificado com sucesso!'
              : 'Não foi possível validar este certificado.'
            }
          </p>

          {/* Credencial ID */}
          <div className="bg-slate-800/50 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-xs text-slate-400 mb-2">Credencial ID:</p>
            <div className="flex items-center justify-center gap-2">
              <p className="text-sm text-slate-300 font-mono">{credentialId}</p>
              <Button
                size="sm"
                variant="ghost"
                onClick={copiarCredencial}
                className="text-slate-400 hover:text-white"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Resultado da Validação */}
        {error ? (
          <Card className="bg-gradient-to-br from-red-900/50 via-red-800/30 to-red-900/50 border border-red-700/40 backdrop-blur-md">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">
                Certificado Inválido
              </h2>
              <p className="text-red-300 text-lg mb-6">
                {error}
              </p>
              <div className="bg-red-800/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-200">
                  Este certificado pode ter sido:
                </p>
                <ul className="text-sm text-red-200 mt-2 space-y-1">
                  <li>• Não emitido por nossa plataforma</li>
                  <li>• Falsificado ou alterado</li>
                  <li>• Revogado ou expirado</li>
                </ul>
              </div>
              <Button 
                variant="outline"
                className="border-red-500/50 text-red-300 hover:bg-red-800/20"
                onClick={() => window.location.href = '/'}
              >
                Voltar ao Início
              </Button>
            </CardContent>
          </Card>
        ) : valido && certificado ? (
          <Card className="bg-gradient-to-br from-green-900/30 via-slate-800/50 to-green-900/30 border border-green-700/40 backdrop-blur-md overflow-hidden">
            <CardContent className="p-0">
              {/* Header do Certificado Válido */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-8 text-center relative">
                <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-green-600/20 backdrop-blur-sm"></div>
                <div className="relative">
                  <Shield className="w-16 h-16 text-white mx-auto mb-4" />
                  <h2 className="text-3xl font-bold text-white mb-2">
                    Certificado Válido
                  </h2>
                  <Badge className="bg-white/20 text-white border-0 text-lg px-4 py-1">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Verificado
                  </Badge>
                </div>
              </div>

              {/* Informações do Certificado */}
              <div className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Informações do Aluno */}
                  <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700/30">
                    <div className="flex items-center gap-3 mb-4">
                      <User className="w-6 h-6 text-green-400" />
                      <h3 className="text-xl font-bold text-white">Aluno</h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-2xl font-bold text-green-400">
                        {certificado.nome_aluno}
                      </p>
                      <p className="text-slate-400">
                        {certificado.email_aluno}
                      </p>
                    </div>
                  </div>

                  {/* Informações do Curso */}
                  <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700/30">
                    <div className="flex items-center gap-3 mb-4">
                      <BookOpen className="w-6 h-6 text-blue-400" />
                      <h3 className="text-xl font-bold text-white">Curso</h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xl font-bold text-white">
                        {certificado.nome_curso}
                      </p>
                      <p className="text-slate-400 text-sm line-clamp-3">
                        {certificado.descricao_curso}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Data de Conclusão */}
                <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700/30 text-center">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <Calendar className="w-6 h-6 text-yellow-400" />
                    <h3 className="text-xl font-bold text-white">Data de Conclusão</h3>
                  </div>
                  <p className="text-2xl font-bold text-yellow-400">
                    {certificado.data_conclusao}
                  </p>
                </div>

                {/* Ações */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  <Button 
                    onClick={baixarCertificado}
                    className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white text-lg py-3"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Baixar Certificado
                  </Button>
                  
                  <Button 
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-lg py-3"
                    onClick={() => window.print()}
                  >
                    <Award className="w-5 h-5 mr-2" />
                    Imprimir Página
                  </Button>
                </div>

                {/* Aviso de Autenticidade */}
                <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4 text-center">
                  <p className="text-blue-300 text-sm">
                    <Shield className="w-4 h-4 inline mr-2" />
                    Este certificado foi emitido e verificado pela nossa plataforma de ensino online.
                    A autenticidade pode ser confirmada através desta página usando a credencial ID.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
};

export default ValidarCertificado; 